import { addBreadcrumb, setTag, startSpan } from "./sentry";
import { reportPerformanceMetric } from "./error-reporting";
import env from "@/lib/env";

export function trackRouteChange(pathname: string, prevPathname: string | null): void {
  addBreadcrumb({
    category: "navigation",
    message: `Route: ${pathname}`,
    level: "info",
    data: { pathname, prevPathname },
  });
  setTag("route", pathname);
}

export function trackPageView(pathname: string, searchParams?: string): void {
  addBreadcrumb({
    category: "page_view",
    message: `Page view: ${pathname}${searchParams ? `?${searchParams}` : ""}`,
    level: "info",
    data: { pathname, searchParams },
  });
}

export function trackAction(action: string, details?: Record<string, unknown>): void {
  addBreadcrumb({
    category: "action",
    message: action,
    level: "info",
    data: details,
  });
}

export function trackApiRequest(url: string, method: string, requestId: string): void {
  addBreadcrumb({
    category: "api_request",
    message: `${method} ${url}`,
    level: "info",
    data: { url, method, requestId },
  });
}

export function trackApiResponse(url: string, method: string, status: number, duration: number): void {
  addBreadcrumb({
    category: "api_response",
    message: `${method} ${url} — ${status} (${duration}ms)`,
    level: status >= 400 ? "error" : "info",
    data: { url, method, status, duration },
  });
}

export function trackComponentRender(componentName: string, duration: number): void {
  addBreadcrumb({
    category: "render",
    message: `${componentName} rendered in ${duration}ms`,
    level: duration > 100 ? "warning" : "info",
    data: { componentName, duration },
  });
}

export function trackInteraction(element: string, action: string): void {
  addBreadcrumb({
    category: "interaction",
    message: `${action} on ${element}`,
    level: "info",
    data: { element, action },
  });
}

export function trackFormSubmission(formName: string, fields: string[]): void {
  addBreadcrumb({
    category: "form",
    message: `Form submitted: ${formName}`,
    level: "info",
    data: { formName, fields },
  });
}

export function trackErrorBoundaryError(error: Error, componentStack: string): void {
  addBreadcrumb({
    category: "error_boundary",
    message: error.message,
    level: "error",
    data: { error: error.message, stack: error.stack, componentStack },
  });
}

export function trackAuthEvent(event: "login" | "logout" | "token_refresh" | "session_expired", details?: Record<string, unknown>): void {
  addBreadcrumb({
    category: "auth",
    message: `Auth event: ${event}`,
    level: event === "session_expired" ? "warning" : "info",
    data: { event, ...details },
  });
}

export function trackSearch(query: string, resultCount: number): void {
  addBreadcrumb({
    category: "search",
    message: `Search: "${query}" — ${resultCount} results`,
    level: "info",
    data: { query, resultCount },
  });
}

export function trackFeatureUsage(feature: string): void {
  addBreadcrumb({
    category: "feature",
    message: `Feature used: ${feature}`,
    level: "info",
    data: { feature },
  });
}

export function initRouteTracking(): void {
  if (typeof window === "undefined") return;

  let prevPathname = window.location.pathname;

  const observer = new MutationObserver(() => {
    const currentPathname = window.location.pathname;
    if (currentPathname !== prevPathname) {
      trackRouteChange(currentPathname, prevPathname);
      prevPathname = currentPathname;
    }
  });

  observer.observe(document.body, { childList: true, subtree: true });
}

export function initRequestTracing(): void {
  if (typeof window === "undefined") return;

  const originalFetch = window.fetch;
  window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
    const url = typeof input === "string" ? input : input instanceof URL ? input.href : input.url;
    const method = init?.method ?? "GET";
    const requestId = `req_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
    const startTime = performance.now();

    trackApiRequest(url, method, requestId);

    try {
      const response = await originalFetch(input, init);
      const duration = Math.round(performance.now() - startTime);
      trackApiResponse(url, method, response.status, duration);
      return response;
    } catch (error) {
      const duration = Math.round(performance.now() - startTime);
      trackApiResponse(url, method, 0, duration);
      throw error;
    }
  };
}

export function initPerformanceTracking(): void {
  if (typeof window === "undefined") return;

  if ("PerformanceObserver" in window) {
    try {
      const observer = new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          if (entry.entryType === "largest-contentful-paint") {
            const perfEntry = entry as PerformanceEntry & { startTime: number };
            const value = perfEntry.startTime;
            const rating = value < 2500 ? "good" : value < 4000 ? "needs-improvement" : "poor";
            reportPerformanceMetric("LCP", value, rating);
          }
        }
      });
      observer.observe({ type: "largest-contentful-paint", buffered: true });
    } catch {
      // LCP observer not supported
    }

    try {
      const clsObserver = new PerformanceObserver((list) => {
        let clsValue = 0;
        for (const entry of list.getEntries()) {
          const perfEntry = entry as PerformanceEntry & { hadRecentInput?: boolean; value: number };
          if (!perfEntry.hadRecentInput) {
            clsValue += perfEntry.value;
          }
        }
        const rating = clsValue < 0.1 ? "good" : clsValue < 0.25 ? "needs-improvement" : "poor";
        reportPerformanceMetric("CLS", clsValue * 1000, rating);
      });
      clsObserver.observe({ type: "layout-shift", buffered: true });
    } catch {
      // CLS observer not supported
    }

    try {
      const fidObserver = new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          const perfEntry = entry as PerformanceEntry & { startTime: number };
          const value = perfEntry.startTime;
          const rating = value < 100 ? "good" : value < 300 ? "needs-improvement" : "poor";
          reportPerformanceMetric("FID", value, rating);
        }
      });
      fidObserver.observe({ type: "first-input", buffered: true });
    } catch {
      // FID observer not supported
    }

    try {
      const ttfbObserver = new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          const perfEntry = entry as PerformanceNavigationTiming;
          const value = perfEntry.responseStart;
          const rating = value < 800 ? "good" : value < 1800 ? "needs-improvement" : "poor";
          reportPerformanceMetric("TTFB", value, rating);
        }
      });
      ttfbObserver.observe({ type: "navigation", buffered: true });
    } catch {
      // TTFB observer not supported
    }
  }
}

export function initUserActionTracking(): void {
  if (typeof window === "undefined") return;

  document.addEventListener("click", (event) => {
    const target = event.target as HTMLElement;
    const element = target.tagName.toLowerCase();
    const text = target.textContent?.slice(0, 100) ?? "";
    const ariaLabel = target.getAttribute("aria-label");
    const id = target.id;
    const className = target.className;

    trackInteraction(`${element}${id ? `#${id}` : ""}${className ? `.${className.split(" ")[0]}` : ""}`, "click");
  });

  document.addEventListener("submit", (event) => {
    const form = event.target as HTMLFormElement;
    const formName = form.name || form.id || "unnamed";
    const fields = Array.from(form.elements)
      .filter((el): el is HTMLInputElement => el instanceof HTMLInputElement)
      .map((el) => el.name)
      .filter(Boolean);

    trackFormSubmission(formName, fields);
  });
}